import { ApiError } from '@/lib/api'
import { type ApprovalItem, type ChatCard, decideCard, fetchApprovals } from '@/lib/ai-team'
import { queryClient } from '@/lib/query'
import { cn } from '@/lib/utils'
import { useQuery } from '@tanstack/react-query'
import { AnimatePresence, motion } from 'framer-motion'
import { Check, ChevronDown, ChevronLeft, ChevronRight, Loader2, X } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { AgentAvatar } from './AgentAvatar'
import { ActionCard } from './ChatMessages'
import { DECIDE_EVENT } from './decisions'

const money = (n: number) => `$${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`

/** One stop in the bar: a single decision, or several of the same kind from the same teammate. */
export type Entry =
  | { key: string; kind: 'one'; item: ApprovalItem }
  | { key: string; kind: 'group'; label: string; items: ApprovalItem[] }

export function refresh() {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: ['ai-approvals'] }),
    queryClient.invalidateQueries({ queryKey: ['ai-room'] }),
    queryClient.invalidateQueries({ queryKey: ['ai-rooms'] }),
    queryClient.invalidateQueries({ queryKey: ['ai-inbox'] })
  ])
}

/** Small, reversible, not money: the same request several times can be decided together. */
function groupable(i: ApprovalItem) {
  return i.kind === 'approval' && !i.money && !!i.action
}

export function entriesOf(items: ApprovalItem[]): Entry[] {
  const out: Entry[] = []
  const groups = new Map<string, ApprovalItem[]>()
  for (const i of items) {
    if (!groupable(i)) continue
    const k = `${i.agent.key}|${i.action}`
    groups.set(k, [...(groups.get(k) ?? []), i])
  }
  const placed = new Set<string>()
  for (const i of items) {
    const k = `${i.agent.key}|${i.action}`
    const group = groupable(i) ? (groups.get(k) ?? []) : []
    if (group.length > 1) {
      if (placed.has(k)) continue
      placed.add(k)
      out.push({ key: `g:${k}`, kind: 'group', label: i.action, items: group })
    } else {
      out.push({ key: `c:${i.id}`, kind: 'one', item: i })
    }
  }
  return out
}

async function decideOne(item: ChatCard, decision: 'approve' | 'reject' | 'answer') {
  const card = await decideCard(item.id, decision, '')
  if (card.status === 'failed') throw new Error(card.error || 'It was approved, but the action failed.')
  return card
}

/**
 * The one place to decide: pinned above the chat box, it shows what the team is waiting on (from any
 * chat, most urgent first). Decide, and the next one slides in. Hidden when nothing is waiting.
 */
export function DecisionDock({ roomId }: { roomId: number }) {
  const query = useQuery({ queryKey: ['ai-approvals'], queryFn: fetchApprovals, refetchInterval: 5_000 })
  const entries = useMemo(() => entriesOf(query.data?.items ?? []), [query.data])
  const [currentKey, setCurrentKey] = useState<string | null>(null)
  const [expanded, setExpanded] = useState(false)
  const [pending, setPending] = useState<string | null>(null)
  const primary = useRef<HTMLButtonElement>(null)

  // The card on screen stays on screen while new ones arrive; only deciding or the arrows move it.
  const index = Math.max(0, entries.findIndex((e) => e.key === currentKey))
  const entry = entries[index]
  useEffect(() => {
    if (entry && entry.key !== currentKey) setCurrentKey(entry.key)
  }, [entry, currentKey])

  useEffect(() => {
    const onFocus = (e: Event) => {
      const id = (e as CustomEvent<number>).detail
      const found = entries.find((x) =>
        x.kind === 'one' ? x.item.id === id : x.items.some((i) => i.id === id)
      )
      if (!found) {
        toast('That one was already decided.')
        refresh()
        return
      }
      setCurrentKey(found.key)
      setExpanded(true)
    }
    window.addEventListener(DECIDE_EVENT, onFocus)
    return () => window.removeEventListener(DECIDE_EVENT, onFocus)
  }, [entries])

  if (!entry) return null

  const go = (step: number) => {
    setCurrentKey(entries[(index + step + entries.length) % entries.length].key)
    setExpanded(false)
  }

  async function run(label: string, cards: ApprovalItem[], decision: 'approve' | 'reject' | 'answer') {
    setPending(label)
    let failed = 0
    let firstError = ''
    for (const c of cards) {
      try {
        await decideOne(c, decision)
      } catch (err) {
        if (err instanceof ApiError && err.status === 409) continue // decided elsewhere meanwhile
        failed++
        firstError ||= err instanceof Error ? err.message : String(err)
      }
    }
    if (failed) toast.error(failed === cards.length ? 'It didn’t work' : `${failed} didn’t work`, { description: firstError })
    else if (cards.length > 1) toast.success(`${decision === 'approve' ? 'Approved' : 'Said no to'} ${cards.length}`)
    const next = entries[index + 1]
    setCurrentKey(next ? next.key : null)
    setExpanded(false)
    setPending(null)
    await refresh()
    primary.current?.focus()
  }

  const items = entry.kind === 'one' ? [entry.item] : entry.items
  const first = items[0]
  const elsewhere = first.room && first.room.id !== roomId
  const urgent = items.some((i) => i.money || i.severity === 'urgent')
  const btn =
    'inline-flex min-h-9 items-center gap-1 rounded-full px-3.5 text-sm font-semibold transition-all active:scale-95 disabled:opacity-50'
  const yesBtn = cn(btn, 'bg-neutral-900 font-bold text-white dark:bg-white dark:text-black')
  const noBtn = cn(
    btn,
    'text-neutral-600 hover:bg-red-50 hover:text-red-600 dark:text-neutral-300 dark:hover:bg-red-500/10 dark:hover:text-red-400'
  )

  return (
    <section
      aria-label="Waiting for your decision"
      className={cn(
        'mb-2 overflow-hidden rounded-[24px] border bg-white shadow-sm dark:bg-neutral-900',
        urgent ? 'border-red-200 dark:border-red-900/60' : 'border-amber-200 dark:border-amber-500/30'
      )}
    >
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 px-3 pt-2 text-xs">
        <span className="relative flex size-2">
          <span className="absolute inline-flex size-full rounded-full bg-amber-400 opacity-60 motion-safe:animate-ping" />
          <span className="relative inline-flex size-2 rounded-full bg-amber-500" />
        </span>
        <span className="font-bold text-neutral-900 dark:text-white" aria-live="polite">
          Needs you
          {entries.length > 1 && (
            <span className="ml-1 font-semibold tabular-nums text-neutral-500 dark:text-neutral-400">
              · {index + 1} of {entries.length}
            </span>
          )}
        </span>
        {entries.length > 1 && (
          <span className="inline-flex items-center">
            <button
              type="button"
              aria-label="Previous decision"
              onClick={() => go(-1)}
              className="flex size-9 items-center justify-center rounded-full text-neutral-500 hover:bg-neutral-100 dark:text-neutral-400 dark:hover:bg-neutral-800"
            >
              <ChevronLeft className="size-4" />
            </button>
            <button
              type="button"
              aria-label="Next decision"
              onClick={() => go(1)}
              className="flex size-9 items-center justify-center rounded-full text-neutral-500 hover:bg-neutral-100 dark:text-neutral-400 dark:hover:bg-neutral-800"
            >
              <ChevronRight className="size-4" />
            </button>
          </span>
        )}
        {elsewhere && first.room && (
          <Link
            to={`/apps/ai-team/chat/${first.room.id}`}
            className="min-w-0 max-w-[40%] truncate rounded-full bg-neutral-100 px-2 py-0.5 font-semibold text-neutral-600 hover:text-neutral-900 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:text-white"
          >
            from {first.room.kind === 'team' ? 'Team room' : first.room.title}
          </Link>
        )}
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          aria-expanded={expanded}
          className="ml-auto inline-flex min-h-9 shrink-0 items-center gap-1 rounded-full px-2.5 font-semibold text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900 dark:text-neutral-400 dark:hover:bg-neutral-800 dark:hover:text-white"
        >
          {expanded ? 'Less' : entry.kind === 'group' ? 'Review' : 'Details'}
          <ChevronDown className={cn('size-3.5 transition-transform', expanded && 'rotate-180')} />
        </button>
      </div>

      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={`${entry.key}-${expanded}`}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.18 }}
        >
          {entry.kind === 'group' ? (
            expanded ? (
              <ul className="max-h-[40vh] space-y-1 overflow-y-auto px-2 pb-2 sm:max-h-[45vh]">
                {entry.items.map((i) => (
                  <li key={i.id} className="flex items-center gap-2 rounded-[24px] px-2 py-1.5 hover:bg-neutral-50 dark:hover:bg-neutral-800/60">
                    <p className="min-w-0 flex-1 truncate text-sm text-neutral-800 dark:text-neutral-200">{i.title}</p>
                    <button type="button" disabled={!!pending} onClick={() => run(`no-${i.id}`, [i], 'reject')} className={noBtn}>
                      {pending === `no-${i.id}` ? <Loader2 className="size-4 animate-spin" /> : <X className="size-4" />}
                      No
                    </button>
                    <button type="button" disabled={!!pending} onClick={() => run(`yes-${i.id}`, [i], 'approve')} className={yesBtn}>
                      {pending === `yes-${i.id}` ? <Loader2 className="size-4 animate-spin" /> : <Check className="size-4" />}
                      Approve
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="flex flex-wrap items-center gap-3 px-3 pb-3 pt-2">
                <AgentAvatar agentKey={first.agent.key} name={first.agent.name} size="sm" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-neutral-900 dark:text-white">
                    {entry.label} <span className="tabular-nums text-neutral-500">× {entry.items.length}</span>
                  </p>
                  <p className="truncate text-xs text-neutral-500 dark:text-neutral-400">
                    {first.agent.name} · {entry.items.slice(0, 2).map((i) => i.title).join(' · ')}
                    {entry.items.length > 2 ? ' …' : ''}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  <button type="button" disabled={!!pending} onClick={() => run('no-all', entry.items, 'reject')} className={noBtn}>
                    {pending === 'no-all' ? <Loader2 className="size-4 animate-spin" /> : <X className="size-4" />}
                    No to all
                  </button>
                  <button
                    ref={primary}
                    type="button"
                    disabled={!!pending}
                    onClick={() => run('yes-all', entry.items, 'approve')}
                    className={yesBtn}
                  >
                    {pending === 'yes-all' ? <Loader2 className="size-4 animate-spin" /> : <Check className="size-4" />}
                    Approve all {entry.items.length}
                  </button>
                </div>
              </div>
            )
          ) : expanded || first.kind === 'question' || first.money ? (
            // Questions need an answer and money needs a careful look: always the full card, never one tap.
            <div className="max-h-[40vh] overflow-y-auto p-2 sm:max-h-[45vh]">
              <ActionCard card={first} agentName={first.agent.name} roomId={first.room?.id} />
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-3 px-3 pb-3 pt-2">
              <AgentAvatar agentKey={first.agent.key} name={first.agent.name} size="sm" />
              <div className="min-w-0 flex-1">
                <p className="line-clamp-2 text-sm font-semibold text-neutral-900 dark:text-white">{first.title}</p>
                <p className="truncate text-xs text-neutral-500 dark:text-neutral-400">
                  {first.agent.name}
                  {first.price?.amount ? ` · ${money(first.price.amount)}` : first.why ? ` · ${first.why}` : ''}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                {first.kind === 'approval' && (
                  <>
                    <button type="button" disabled={!!pending} onClick={() => run('no', [first], 'reject')} className={noBtn}>
                      {pending === 'no' ? <Loader2 className="size-4 animate-spin" /> : <X className="size-4" />}
                      No
                    </button>
                    <button
                      ref={primary}
                      type="button"
                      disabled={!!pending}
                      onClick={() => run('yes', [first], 'approve')}
                      className={yesBtn}
                    >
                      {pending === 'yes' ? <Loader2 className="size-4 animate-spin" /> : <Check className="size-4" />}
                      Approve
                    </button>
                  </>
                )}
                {first.kind === 'alert' && (
                  <button
                    ref={primary}
                    type="button"
                    disabled={!!pending}
                    onClick={() => run('ok', [first], 'answer')}
                    className={yesBtn}
                  >
                    {pending ? <Loader2 className="size-4 animate-spin" /> : <Check className="size-4" />}
                    Got it
                  </button>
                )}
              </div>
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    </section>
  )
}

